import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { ActionCreator, AnyAction } from 'redux';

import { NO_FILTER_OPTION_LABEL } from '../../helpers/savedFilters';
import * as IssuesV2Store from '../../redux/issuesV2';
import * as SavedFiltersStore from '../../redux/savedFilters';
import { type ResourceOption, SavedResourceDropdownList } from '../generic/SavedResources/SavedResourceDropdownList';
import useFetchIssuesAction from '../issues/IssuesInbox/hooks/useFetchIssuesAction';
import type { ReduxStore } from '../../redux/store';

import useSavedFilters from './hooks/useSavedFilters';

const UNFILTERED_OPTION: ResourceOption = {
  id: null,
  name: NO_FILTER_OPTION_LABEL,
  isProtected: true,
};
/**
 * SavedFiltersDropdownListContainer is responsible for a few things:
 *   - Fetching the list of saved filters for this org + issueCategory combo.
 *   - Handle dispatching the action which applies the current filter (and in turn re-fetches the issues).
 */
const SavedFiltersDropdownListContainer: VoidFunctionComponent = () => {
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const {
    filters,
    status: { loading: isLoading },
  } = useSavedFilters(category);
  const currentFilter = useSelector((state: ReduxStore) =>
    SavedFiltersStore.selectors.getCurrentFilterByCategory(state, category),
  );
  //  Wrapped in a useFetchIssuesAction so that we re-fetch issues when the currentFilter is reset
  const setCurrentFilter = useFetchIssuesAction(
    SavedFiltersStore.actions.setCurrentFilter as unknown as ActionCreator<AnyAction>,
  );

  const filterOptions: ResourceOption[] = [UNFILTERED_OPTION, ...filters.map(({ id, name }) => ({ id, name }))];

  const onChange = (nextFilterId: number) => {
    //  Handle "unsetting" the saved filter.
    if (nextFilterId === null) {
      setCurrentFilter(null);
      return;
    }
    //  If the filter is actually changing, dispatch it
    if (nextFilterId !== currentFilter?.id) {
      setCurrentFilter(nextFilterId);
    }
  };

  return (
    <SavedResourceDropdownList
      resourceType="filter"
      resources={filterOptions}
      currentResource={currentFilter || UNFILTERED_OPTION}
      isLoading={isLoading}
      onChange={(filter) => onChange(filter as number)}
    />
  );
};

export default SavedFiltersDropdownListContainer;
